<template>
    <v-container v-if="eventSlot">
        <v-row>
            <v-col cols="12" order-md="2">
                <div class="d-flex justify-center justify-md-end">
                    <action-buttons
                        :state="editState"
                        :permissions="userPermission"
                        :disabled="isLoading"
                        @save="handleSave"
                        @cancel="handleCancel"
                        @close="handleClose"
                        :is-dirty="isDirty"
                        class="d-flex justify-end"
                    />
                </div>
            </v-col>
        </v-row>
        <v-form ref="form" v-model="valid">
            <v-row>
                <v-col cols="12" sm="4">
                    <v-select
                        v-model="eventSlot.eventId"
                        :rules="[requiredRule]"
                        :items="events"
                        item-text="name"
                        item-value="id"
                        :disabled="isLoading"
                        label="Event"
                        outlined
                        dense
                        @change="eventIdChanged"
                    >
                        <template v-slot:[`label`]>
                            <label-required name="Event" />
                        </template>
                    </v-select>
                </v-col>
                <v-col cols="12" sm="4">
                    <v-select
                        v-model="eventSlot.eventSlotType"
                        :rules="[requiredRule]"
                        :items="eventSlotTypes"
                        :disabled="isLoading"
                        label="Event Slot Type"
                        outlined
                        dense
                        @change="setDefaultDuration"
                    >
                        <template v-slot:[`label`]>
                            <label-required name="Event Slot Type" />
                        </template>
                    </v-select>
                </v-col>
            </v-row>
            <v-row
                ><v-col cols="4">
                    <v-text-field
                        v-model="dayAndTime"
                        outlined
                        readonly
                        dense
                        label="Encounter Day and Time"
                        @click="showSlotPicker = true"
                    >
                        <template v-slot:[`label`]>
                            <label-required name="Encounter Day and Time" />
                        </template>
                    </v-text-field>
                </v-col>
                <v-col cols="3">
                    <time-selector
                        v-model="eventSlot.durationMinutes"
                        label="Duration"
                        :zero-pad-display="false"
                    ></time-selector>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="4">
                    <v-select
                        v-model="eventSlot.teamId"
                        :items="teams"
                        label="Assigned Team"
                        item-text="name"
                        item-value="id"
                        outlined
                        dense
                        clearable
                    ></v-select>
                </v-col>
                <v-col cols="12" md="4">
                    <v-textarea v-model="eventSlot.comment" label="Comment/Notes" outlined dense clearable></v-textarea>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="3">
                    <v-checkbox v-model="eventSlot.isPublished" label="Is Published"></v-checkbox>
                </v-col>
                <v-col cols="12" md="3">
                    <v-checkbox v-model="eventSlot.isSharedWithAll" label="Is Shared With All"></v-checkbox>
                </v-col>
            </v-row>
        </v-form>
        <v-dialog v-model="showSlotPicker" max-width="900">
            <v-card tile data-card>
                <v-toolbar dark color="secondary">
                    <v-btn icon dark data-close @click="showSlotPicker = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <v-toolbar-title>{{ event ? event.name : '' }} - Select Encounter Time</v-toolbar-title>
                </v-toolbar>
                <v-card-text>
                    <event-time-table
                        :event="event"
                        @slotSelected="(eventDayId, time) => handleSlotSelected(eventDayId, time)"
                        :slot-data="slotData"
                        is-select-mode
                    ></event-time-table>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-container>
</template>
<script lang="js">
import api from '@/api'
import { DateTime } from 'luxon'
import RequiresLabel from '../../components/RequiredLabel'
import navGuardConfirmMixin from '../../mixins/navGuardConfirmMixin'
import editorMixin from '../../mixins/editorMixin'
import TimeSelector from '@/components/TimeSelector'
import EventTimeTable from '../../components/EventTimeTable.vue'
export default {
    name: 'EventSlotEditor',
    mixins: [navGuardConfirmMixin, editorMixin],
    components: {
        'label-required': RequiresLabel,
        'time-selector': TimeSelector,
        'event-time-table': EventTimeTable,
    },
    data() {
        return {
            valid: true,
            eventSlot: null,
            event: null,
            dayAndTime: null,
            showSlotPicker: false,
            slotData: [],
            startPicker: false,
            dateRange: [],
            tempDateRange: [],
            days: [],
            startMinutes: '',
            endMinutes: '',
            defaultTeamId: 0,
            teams: [],
            validation: {
                nameMessage: '',
                datesMessage: '',
            },
            eventSlotTypes: [
                { text: 'Unknown', value: 0 },
                { text: 'Monster Slot', value: 1 },
                { text: 'Ritual Slot', value: 2 },
                { text: 'Mass Combat', value: 3 },
                { text: 'Linear Slot', value: 4 },
            ],
        }
    },
    props: {
        id: {
            type: [String, Number],
            required: false,
            default: undefined,
        },
        startingEvent: {
            type: Number,
            required: false,
            default: null,
        },
        startingDay: {
            type: Number,
            required: false,
            default: null,
        },
        startingTime: {
            type: Number,
            required: false,
            default: null,
        },
    },
    watch: {
        eventSlot: {
            deep: true,
            handler() {
                this.isDirty = true
            },
        },
        days: {
            deep: true,
            handler() {
                this.isDirty = true
            },
        },
        startPicker() {
            this.tempDateRange = this.dateRange
        },
    },
    computed: {
        userPermission() {
            if (this.user && this.user.editEvents) {
                return this.user.editEvents.all
            } else {
                return 0
            }
        },
        editState() {
            return this.id ? 'Edit' : 'Create'
        },
    },
    mounted() {
        this.getTeams()
        if (this.id) {
            this.getEventSlot()
        } else {
            this.newEventSlot()
        }
    },
    methods: {
        getEventSlot() {
            this.isLoading = true
            api.get(`eventSlots/${this.id}`)
                .then(res => {
                    this.eventSlot = res
                    this.endMinutes = res.timeAsMinutes + res.durationMinutes
                    this.loaded()
                })
                .finally(() => {
                    this.isDirty = false
                    this.isLoading = false
                })
        },
        loaded() {
            if (this.eventSlot && this.eventSlot.eventId) {
                let selectedEvent = this.events.find(x => x.id === this.eventSlot.eventId)
                if (!selectedEvent) {
                    this.event = null
                    this.dayAndTime = null
                    return
                }
                this.event = selectedEvent
                let day = selectedEvent.days.find(x => x.id === this.eventSlot.eventDayId)

                if (!day || !this.isValidDate(day.date)) {
                    this.dayAndTime = null
                } else {
                    let dateOnly = DateTime.fromISO(day.date, { zone: 'utc' })
                    if (this.eventSlot.timeAsMinutes) {
                        let dateTime = dateOnly.plus({ minutes: this.eventSlot.timeAsMinutes })
                        this.dayAndTime = `${dateOnly.toFormat('cccc')} ${dateTime.toLocaleString(
                            DateTime.TIME_24_SIMPLE,
                        )}`
                    } else {
                        this.dayAndTime = `${dateOnly.toFormat('cccc')} - Floating`
                    }
                }
                this.slotData = [
                    {
                        dayId: this.eventSlot.eventDayId,
                        time: this.eventSlot.timeAsMinutes,
                        text: 'Current',
                        id: this.eventSlot.keyId,
                    },
                ]
            }
        },
        newEventSlot() {
            this.eventSlot = {
                eventId: this.startingEvent ?? this.nextEvent,
                eventDayId: this.startingDay ?? 0,
                timeAsMinutes: this.startingTime ?? null,
                durationMinutes: 0,
            }
            this.loaded();
        },
        getTeams() {
            if (this.teams && this.teams.length > 0) return
            this.isLoading = true
            api.getTeams()
                .then(res => {
                    this.teams = Object.freeze(res)
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        handleSave(isClose) {
            if(!this.validate()) return
            this.isLoading = true
            if(this.id) {
                api.put(`eventSlots/${this.id}`, this.eventSlot)
                    .then(() => {
                        this.showSnackbar('success', `Event Slot Updated`)
                        this.isDirty = false
                    })
                    .finally(() => {
                        this.isLoading = false
                        if (isClose) {
                            this.handleClose()
                        }
                    })
            } else {
                api.post(`eventSlots`, this.eventSlot)
                    .then(() => {
                        this.showSnackbar('success', `Event Slot Created`)
                        this.isDirty = false
                    })
                    .finally(() => {
                        this.isLoading = false
                        if (isClose) {
                            this.handleClose()
                        }
                    })
            }
        },
        handleCancel() {
            window.history.length > 1 ? this.$router.go(-1) : this.$router.push('/')
        },
        handleClose() {
            window.history.length > 1 ? this.$router.go(-1) : this.$router.push('/')
        },
        eventIdChanged(val) {
            let selectedEvent = this.events.find(x => x.id === val)
            if (!selectedEvent) {
                this.event = null
                this.dayAndTime = null
                return
            }
            this.event = selectedEvent
            this.dayAndTime = null
        },
        handleSlotSelected(eventDayId, time) {
            this.eventSlot.eventDayId = eventDayId
            this.eventSlot.timeAsMinutes = time
            this.loaded()
            this.showSlotPicker = false
        },
        isValidDate(value) {
            if (!value || value === 'Invalid DateTime' || (DateTime.isDateTime(value) && !value.isValid)) {
                return false
            }
            return true
        },
        setDefaultDuration() {
            switch(this.eventSlot.eventSlotType) {
                case 1:
                    this.eventSlot.durationMinutes = 120
                    break;
                case 2:
                    this.eventSlot.durationMinutes = 30
                    break;
                default:
                    this.eventSlot.durationMinutes = 60
            }
        },
        validate() {
            return this.$refs.form.validate()
        },
    },
}
</script>
